import { ProjectPlanCumulation } from '../../../../lib/functions/projectPlan'
import {
  ProgramManagementProfitLoss,
  ProgramManagementProgress,
  ProgramManagementProgressSet,
  ProgramManagementResource,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'

const sumProgress = (
  items: ProgramManagementProgress[]
): ProgramManagementProgress => ({
  total: items.reduce((s, p) => s + p.total, 0),
  scheduledCompleted: items.reduce((s, p) => s + p.scheduledCompleted, 0),
  completed: items.reduce((s, p) => s + p.completed, 0),
  delayed: items.reduce((s, p) => s + p.delayed, 0),
  preceding: items.reduce((s, p) => s + p.preceding, 0),
})

const sumProgressSet = (
  children: ProgramManagementRow[]
): ProgramManagementProgressSet | undefined => {
  const withProgress = children.filter(c => c.progress)
  if (withProgress.length === 0) return undefined
  return {
    deliverableCount: sumProgress(
      withProgress.map(c => c.progress!.deliverableCount)
    ),
    deliverableWorkload: sumProgress(
      withProgress.map(c => c.progress!.deliverableWorkload)
    ),
    taskCount: sumProgress(withProgress.map(c => c.progress!.taskCount)),
    taskWorkload: sumProgress(withProgress.map(c => c.progress!.taskWorkload)),
  }
}

const sumResource = (
  children: ProgramManagementRow[]
): ProgramManagementResource | undefined => {
  const withResource = children.filter(c => c.resource)
  if (withResource.length === 0) return undefined
  return {
    totalResource: withResource.reduce(
      (s, c) => s + c.resource!.totalResource,
      0
    ),
    investedResource: withResource.reduce(
      (s, c) => s + c.resource!.investedResource,
      0
    ),
    scheduledResource: withResource.reduce(
      (s, c) => s + c.resource!.scheduledResource,
      0
    ),
    actualResource: withResource.reduce(
      (s, c) => s + c.resource!.actualResource,
      0
    ),
  }
}

const sumCumulation = (
  children: ProgramManagementRow[]
): ProjectPlanCumulation | undefined => {
  const cumulations = children
    .map(c => c.projectPlanCumulation)
    .filter((c): c is ProjectPlanCumulation => c !== undefined)
  if (cumulations.length === 0) return undefined
  return ProjectPlanCumulation.sum(cumulations)
}

/**
 * 配下の最大予定終了日（超過＝黄色ハッチ表示用）を求める。
 * 子自身の予定終了日と、子の cumulation が持つ最大予定終了日（さらに配下分）の最大値。
 * ProjectPlanCumulation.sum は日付を集計しないため別途算出する。
 */
const maxScheduledEndDateOf = (
  children: ProgramManagementRow[]
): string | undefined => {
  const dates: string[] = []
  for (const child of children) {
    const ownEnd = child.scheduledDate?.endDate
    if (ownEnd) dates.push(ownEnd.replace(/\//g, '-'))
    const childMax = child.projectPlanCumulation?.maxScheduledEndDate
    if (childMax) dates.push(childMax)
  }
  if (dates.length === 0) return undefined
  return dates.sort()[dates.length - 1]
}

const sumProfitLoss = (
  children: ProgramManagementRow[]
): ProgramManagementProfitLoss | undefined => {
  const withProfitLoss = children.filter(c => c.profitLoss)
  if (withProfitLoss.length === 0) return undefined
  return {
    totalBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.totalBudget,
      0
    ),
    scheduledUsedBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.scheduledUsedBudget,
      0
    ),
    actualUsedBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.actualUsedBudget,
      0
    ),
    remainingBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.remainingBudget,
      0
    ),
    totalRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.totalRevenue,
      0
    ),
    scheduledRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.scheduledRevenue,
      0
    ),
    actualRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.actualRevenue,
      0
    ),
    forecastRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.forecastRevenue,
      0
    ),
  }
}

/**
 * 子の予定日から親の予定日を集計する。
 * 子の startDate の最小値 → 親の startDate
 * 子の endDate の最大値 → 親の endDate
 */
const aggregateScheduledDate = (
  children: ProgramManagementRow[]
): { startDate?: string; endDate?: string } | undefined => {
  const dates = children.filter(c => c.scheduledDate).map(c => c.scheduledDate!)

  if (dates.length === 0) return undefined

  const startDates = dates.map(d => d.startDate).filter((d): d is string => !!d)
  const endDates = dates.map(d => d.endDate).filter((d): d is string => !!d)

  return {
    startDate: startDates.length > 0 ? startDates.sort()[0] : undefined,
    endDate:
      endDates.length > 0 ? endDates.sort()[endDates.length - 1] : undefined,
  }
}

/**
 * 予定日を配下から集計すべき行かどうか。
 * 原則は各行が持つ自身の予定日を表示する（PROJECT/WORKGROUP/PROCESS は常に自身の予定日）。
 * PROGRAM/GROUP のみ、自身の予定日が未設定の場合に配下から集計する。
 */
/** 予定日を配下から補完できる行か（PROGRAM/GROUP は自身の予定日が任意のため補完対象） */
const canAggregateScheduledDate = (row: ProgramManagementRow): boolean =>
  row.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM ||
  row.type === PROGRAM_MANAGEMENT_ROW_TYPE.GROUP

/**
 * フラットな行配列に対してボトムアップ集計を行う。
 * treeValue から親子関係を復元し、深い行から順に処理することで
 * 子の集計値が親に正しく伝播する。
 *
 * - scheduledDate: GROUP/PROGRAM行で手動入力されていなければ子から集計
 * - progress: 自身に値がなければ直下の子から集計
 * - resource: 自身に値がなければ直下の子から集計
 * - profitLoss: 自身に値がなければ直下の子から集計
 */
export const aggregateTreeData = (
  rows: ProgramManagementRow[]
): ProgramManagementRow[] => {
  // treeValue から親子関係を構築
  const childrenByParentUuid = new Map<string, string[]>()
  for (const row of rows) {
    if (row.treeValue.length >= 2) {
      const parentUuid = row.treeValue[row.treeValue.length - 2]
      const siblings = childrenByParentUuid.get(parentUuid) ?? []
      siblings.push(row.uuid)
      childrenByParentUuid.set(parentUuid, siblings)
    }
  }

  // uuid → row のルックアップ（集計結果を格納する mutable コピー）
  const rowMap = new Map<string, ProgramManagementRow>(
    rows.map(r => [r.uuid, { ...r }])
  )

  // 深い行から順に処理（ボトムアップ）
  const sortedRows = [...rows].sort(
    (a, b) => b.treeValue.length - a.treeValue.length
  )

  for (const row of sortedRows) {
    const childUuids = childrenByParentUuid.get(row.uuid)
    if (!childUuids || childUuids.length === 0) {
      // 配下が無い行は集計元が無く cumulation が undefined になる。
      // ガント描画等が常に cumulation 存在を前提に動けるよう、タイプを問わず空の cumulation を保証する。
      const leaf = rowMap.get(row.uuid)!
      if (!leaf.projectPlanCumulation) {
        leaf.projectPlanCumulation = new ProjectPlanCumulation()
      }
      continue
    }

    const children = childUuids
      .map(uuid => rowMap.get(uuid))
      .filter((r): r is ProgramManagementRow => r !== undefined)

    const updated = rowMap.get(row.uuid)!

    // 予定日: 原則は各行が持つ自身の予定日を表示する。
    // PROGRAM/GROUP は自身の値が無いフィールド（開始/終了それぞれ）のみ配下から補完し、
    // 補完したフィールドにはフラグを立てて表示側で薄グレー文字にする。
    // 補完値は「自身の値」とみなさず、毎回集計し直す（配下の変更追従・クリア残骸防止）。
    if (canAggregateScheduledDate(updated)) {
      const aggregated = aggregateScheduledDate(children)
      const ownStart = updated.scheduledStartDateAggregated
        ? undefined
        : updated.scheduledDate?.startDate
      const ownEnd = updated.scheduledEndDateAggregated
        ? undefined
        : updated.scheduledDate?.endDate
      const startDate = ownStart ?? aggregated?.startDate
      const endDate = ownEnd ?? aggregated?.endDate
      updated.scheduledDate =
        startDate || endDate ? { startDate, endDate } : undefined
      updated.scheduledStartDateAggregated = !ownStart && !!startDate
      updated.scheduledEndDateAggregated = !ownEnd && !!endDate
    } else {
      updated.scheduledStartDateAggregated = false
      updated.scheduledEndDateAggregated = false
    }

    if (!updated.progress) updated.progress = sumProgressSet(children)
    if (!updated.resource) updated.resource = sumResource(children)
    if (!updated.profitLoss) updated.profitLoss = sumProfitLoss(children)
    if (!updated.projectPlanCumulation) {
      // 子に cumulation が無く集計結果が空でも、cumulation は必ず存在させる。
      const summed = sumCumulation(children) ?? new ProjectPlanCumulation()
      summed.maxScheduledEndDate = maxScheduledEndDateOf(children)
      updated.projectPlanCumulation = summed
    }
  }

  // 元の配列順序を維持して返す
  return rows.map(r => rowMap.get(r.uuid) ?? r)
}
